import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthSessionService } from '@core/auth/auth-session.service';
import { PermissionContextService } from '@core/auth/permission-context.service';
import { AdminShellModeService } from '@core/shell/admin-shell-mode.service';
import { LocationContextService } from '@core/tenant/location-context.service';
import { TenantContextService } from '@core/tenant/tenant-context.service';
import { AuthService } from '@features/auth/public-api';
import { createStaffAuthUrlTree } from '@features/auth/utils/create-staff-auth-url-tree.util';
import {
  refineTenantEntryUrlTree,
  shouldRefineTenantEntryUrl,
} from '@features/auth/utils/navigate-after-staff-auth.util';
import { Observable, catchError, of, switchMap } from 'rxjs';

/**
 * Rutas solo para invitados (login/registro).
 * Si hay JWT válido, redirige al destino post-auth; si el token expiró, limpia sesión y deja pasar.
 */
export const guestGuard: CanActivateFn = () => {
  const authSession = inject(AuthSessionService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const tenantContext = inject(TenantContextService);
  const locationContext = inject(LocationContextService);
  const adminShellMode = inject(AdminShellModeService);
  const permissionContext = inject(PermissionContextService);

  if (!authSession.isAuthenticated()) {
    return true;
  }

  const redirect = (
    user: NonNullable<ReturnType<AuthSessionService['user']>>
  ): UrlTree | Observable<UrlTree> => {
    const tree = createStaffAuthUrlTree({
      user,
      router,
      tenantContext,
      locationContext,
      adminShellMode,
    });
    if (!shouldRefineTenantEntryUrl(user)) {
      return tree;
    }
    return refineTenantEntryUrlTree({ user, router, permissionContext, adminShellMode });
  };

  const user = authSession.user();
  if (user !== null) {
    return redirect(user);
  }

  return authService.loadCurrentUser().pipe(
    switchMap((loaded) => {
      const next = redirect(loaded);
      return next instanceof Observable ? next : of(next);
    }),
    catchError(() => {
      authSession.clear();
      tenantContext.clear();
      locationContext.clear();
      return of(true);
    })
  );
};
